import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

function PrintReport() {
  const location = useLocation();
  const { 
    soldProducts, 
    totalRevenue, 
    totalPurchase, 
    totalProfit, 
    totalQuantity,
    filters,
    dateRange
  } = location.state || {};
  
  const printRef = useRef();

  useEffect(() => {
    // الطباعة التلقائية عند فتح الصفحة
    window.print();
    
    // العودة للصفحة السابقة بعد الطباعة أو الإلغاء
    const handleAfterPrint = () => {
      window.close();
    };
    
    window.addEventListener('afterprint', handleAfterPrint);
    return () => window.removeEventListener('afterprint', handleAfterPrint);
  }, []);

  const formatMoney = (amount) => {
    const num = Number(amount) || 0;
    return num.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",") + " DA";
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString();
  };

  return (
    <div ref={printRef} style={styles.printContainer}>
      {/* رأس التقرير */}
      <div style={styles.header}>
        <h1 style={styles.title}>📊 Rapport des Ventes</h1>
        <p style={styles.datePrint}>
          Généré le: {new Date().toLocaleDateString()} à {new Date().toLocaleTimeString()}
        </p>
      </div>

      {/* معلومات الفلترة */}
      {(filters || dateRange) && (
        <div style={styles.filtersSection}>
          <h3 style={styles.sectionTitle}>🔍 Filtres appliqués:</h3>
          <div style={styles.filtersGrid}>
            {filters?.selectedStatus && filters.selectedStatus !== 'Tous' && (
              <div style={styles.filterItem}>Statut: <strong>{filters.selectedStatus}</strong></div>
            )}
            {filters?.selectedBrand && filters.selectedBrand !== 'Tous' && (
              <div style={styles.filterItem}>Marque: <strong>{filters.selectedBrand}</strong></div>
            )}
            {dateRange?.start && (
              <div style={styles.filterItem}>Du: <strong>{formatDate(dateRange.start)}</strong></div>
            )}
            {dateRange?.end && (
              <div style={styles.filterItem}>Au: <strong>{formatDate(dateRange.end)}</strong></div>
            )}
          </div>
        </div>
      )}

      {/* بطاقات الإحصائيات */}
      <div style={styles.statsSection}>
        <h3 style={styles.sectionTitle}>📈 Résumé</h3>
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Chiffre d'affaires</div>
            <div style={styles.statValue}>{formatMoney(totalRevenue)}</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Coût d'achat</div>
            <div style={styles.statValue}>{formatMoney(totalPurchase)}</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Bénéfice</div>
            <div style={styles.statValue}>{formatMoney(totalProfit)}</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Quantité vendue</div>
            <div style={styles.statValue}>{totalQuantity}</div>
          </div>
        </div>
      </div>

      {/* جدول المنتجات مع التفاصيل */}
      <div style={styles.productsSection}>
        <h3 style={styles.sectionTitle}>📦 Détail des produits</h3>
        {soldProducts?.map((product, index) => (
          <div key={index} style={styles.productBlock}>
            {/* معلومات المنتج الأساسية */}
            <div style={styles.productHeader}>
              <div style={styles.productTitle}>
                <strong>{product.name}</strong> - {product.brand}
              </div>
              <div style={styles.productSummary}>
                <span>Prix achat: {formatMoney(product.purchasePrice)}</span>
                <span>Prix vente: {formatMoney(product.salePrice)}</span>
                <span>Quantité totale: {product.quantity}</span>
                <span>Total: {formatMoney(product.total)}</span>
              </div>
            </div>

            {/* جدول تفاصيل الطلبيات لهذا المنتج */}
            <table style={styles.detailsTable}>
              <thead>
                <tr>
                  <th style={{...styles.th, textAlign: 'left'}}>Date</th>
                  <th style={{...styles.th, textAlign: 'left'}}>N° Commande</th>
                  <th style={{...styles.th, textAlign: 'right'}}>Quantité</th>
                  <th style={{...styles.th, textAlign: 'right'}}>Prix Vente</th>
                  <th style={{...styles.th, textAlign: 'right'}}>Prix Achat</th>
                  <th style={{...styles.th, textAlign: 'left'}}>Statut</th>
                </tr>
              </thead>
              <tbody>
                {product.orderDetails?.map((detail, i) => (
                  <tr key={i}>
                    <td style={{...styles.td, textAlign: 'left'}}>
                      {new Date(detail.orderDate).toLocaleDateString()}
                    </td>
                    <td style={{...styles.td, textAlign: 'left'}}>
                      {detail.orderNumber || detail.orderId?.slice(-4) || 'N/A'}
                    </td>
                    <td style={{...styles.td, textAlign: 'right'}}>{detail.quantity}</td>
                    <td style={{...styles.td, textAlign: 'right'}}>{formatMoney(detail.salePrice)}</td>
                    <td style={{...styles.td, textAlign: 'right'}}>{formatMoney(detail.purchasePrice)}</td>
                    <td style={{...styles.td, textAlign: 'left'}}>{detail.status || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      {/* تذييل الصفحة */}
      <div style={styles.footer}>
        <p>Rapport généré automatiquement - Tous droits réservés</p>
      </div>
    </div>
  );
}

const styles = {
  printContainer: {
    padding: '20px',
    fontFamily: 'Arial, sans-serif',
    backgroundColor: 'white',
    maxWidth: '1200px',
    margin: '0 auto'
  },
  header: {
    textAlign: 'center',
    marginBottom: '30px',
    padding: '20px',
    borderBottom: '2px solid #333'
  },
  title: {
    fontSize: '24px',
    margin: '0 0 10px 0',
    color: '#333'
  },
  datePrint: {
    fontSize: '12px',
    color: '#666',
    margin: 0
  },
  filtersSection: {
    marginBottom: '30px',
    padding: '15px',
    backgroundColor: '#f5f5f5',
    borderRadius: '5px'
  },
  sectionTitle: {
    fontSize: '18px',
    margin: '0 0 15px 0',
    color: '#333',
    borderBottom: '1px solid #ddd',
    paddingBottom: '5px'
  },
  filtersGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '10px'
  },
  filterItem: {
    fontSize: '14px',
    color: '#555'
  },
  statsSection: {
    marginBottom: '30px'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '15px'
  },
  statCard: {
    padding: '15px',
    backgroundColor: '#f8f9fa',
    borderRadius: '5px',
    textAlign: 'center'
  },
  statLabel: {
    fontSize: '12px',
    color: '#666',
    marginBottom: '5px'
  },
  statValue: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#333'
  },
  productsSection: {
    marginBottom: '30px'
  },
  productBlock: {
    marginBottom: '25px',
    padding: '15px',
    backgroundColor: '#fff',
    border: '1px solid #ddd',
    borderRadius: '5px'
  },
  productHeader: {
    marginBottom: '15px',
    padding: '10px',
    backgroundColor: '#f0f0f0',
    borderRadius: '5px'
  },
  productTitle: {
    fontSize: '16px',
    marginBottom: '5px',
    color: '#333'
  },
  productSummary: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '10px',
    fontSize: '13px',
    color: '#666'
  },
  detailsTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '12px'
  },
  th: {
    padding: '8px',
    backgroundColor: '#e0e0e0',
    color: '#333',
    fontWeight: 'bold',
    border: '1px solid #ccc'
  },
  td: {
    padding: '6px',
    border: '1px solid #ddd'
  },
  footer: {
    marginTop: '30px',
    padding: '20px',
    textAlign: 'center',
    fontSize: '11px',
    color: '#999',
    borderTop: '1px solid #ddd'
  }
};

// ✅ التصدير الافتراضي - الأهم
export default PrintReport;